import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
  Clock, Check, X, AlertCircle, User, FileCheck, Wallet, ChevronRight 
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export const SupervisorApprovals = () => {
  const { adminApi, admin } = useAuth();
  const [pendingChanges, setPendingChanges] = useState([]);
  const [changeHistory, setChangeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChange, setSelectedChange] = useState(null);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pendingRes, historyRes] = await Promise.all([
        adminApi.get('/supervisor/pending-changes'),
        adminApi.get('/supervisor/change-history')
      ]);
      setPendingChanges(pendingRes.data);
      setChangeHistory(historyRes.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        toast.error('Supervisor access required');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (changeId, action) => {
    setProcessing(true);
    try {
      await adminApi.put(`/supervisor/pending-changes/${changeId}`, {
        action,
        notes
      });
      toast.success(`Change ${action}d successfully`);
      setSelectedChange(null);
      setNotes('');
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} change`);
    } finally {
      setProcessing(false);
    }
  };

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'user_edit':
      case 'user_add':
        return <User className="w-5 h-5 text-blue-600" />;
      case 'kyc_approve':
      case 'kyc_reject':
        return <FileCheck className="w-5 h-5 text-yellow-600" />;
      case 'loan_approve':
      case 'loan_reject':
        return <Wallet className="w-5 h-5 text-green-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatChangeType = (type) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-afriperx-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (admin?.role !== 'supervisor') {
    return (
      <Card className="bg-white border border-gray-200 p-8 text-center rounded-xl">
        <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Supervisor Access Required</h2>
        <p className="text-gray-600">You need supervisor privileges to access this page.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="supervisor-approvals">
      <div>
        <h1 className="font-syne text-2xl font-bold text-gray-900">Pending Approvals</h1>
        <p className="text-gray-600">Review and approve changes submitted by admins</p>
      </div>

      {/* Pending Changes */}
      <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
        <h2 className="font-syne text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-yellow-500" />
          Pending Changes ({pendingChanges.length})
        </h2>
        
        {pendingChanges.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No pending changes to review</p>
        ) : (
          <div className="space-y-3">
            {pendingChanges.map((change) => (
              <div 
                key={change.id} 
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid={`pending-change-${change.id}`}
              >
                <div className="flex items-center gap-4">
                  {getChangeTypeIcon(change.change_type)}
                  <div>
                    <p className="text-gray-900 font-medium">{formatChangeType(change.change_type)}</p>
                    <p className="text-gray-500 text-sm">
                      Requested by {change.requested_by} &bull; {new Date(change.requested_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setSelectedChange(change)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-600 hover:text-gray-900"
                  data-testid={`review-change-${change.id}`}
                >
                  Review
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Recent History */}
      <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
        <h2 className="font-syne text-lg font-bold text-gray-900 mb-4">Recent Decisions</h2>
        {changeHistory.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No history yet</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {changeHistory.slice(0, 20).map((change) => (
              <div key={change.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {change.status === 'approved' ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <X className="w-5 h-5 text-red-600" />
                  )}
                  <div>
                    <p className="text-gray-900 text-sm">{formatChangeType(change.change_type)}</p>
                    <p className="text-gray-500 text-xs">
                      {change.status} by {change.reviewed_by} &bull; {new Date(change.reviewed_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Review Dialog */}
      <Dialog open={!!selectedChange} onOpenChange={() => setSelectedChange(null)}>
        <DialogContent className="bg-white border-gray-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-900 font-syne">Review Change Request</DialogTitle>
          </DialogHeader>
          {selectedChange && (
            <div className="space-y-4 pt-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-500 text-sm mb-2">Change Type</p>
                <p className="text-gray-900 font-medium">{formatChangeType(selectedChange.change_type)}</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-500 text-sm mb-2">Requested By</p>
                <p className="text-gray-900">{selectedChange.requested_by}</p>
                <p className="text-gray-500 text-xs">{new Date(selectedChange.requested_at).toLocaleString()}</p>
              </div>

              {selectedChange.old_data && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-500 text-sm mb-2">Previous Values</p>
                  <pre className="text-gray-700 text-xs overflow-x-auto bg-white p-2 rounded border border-gray-200">
                    {JSON.stringify(selectedChange.old_data, null, 2)}
                  </pre>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-500 text-sm mb-2">New Values</p>
                <pre className="text-green-700 text-xs overflow-x-auto bg-white p-2 rounded border border-gray-200">
                  {JSON.stringify(selectedChange.new_data, null, 2)}
                </pre>
              </div>

              <div>
                <Label className="text-gray-700">Notes (Optional)</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this decision..."
                  className="mt-2 bg-white border-gray-300 text-gray-900"
                  data-testid="supervisor-notes-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleAction(selectedChange.id, 'approve')}
                  disabled={processing}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="approve-change-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button
                  onClick={() => handleAction(selectedChange.id, 'reject')}
                  disabled={processing}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="reject-change-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
